// src/pages/LoginPage.jsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { sendPasswordResetEmail } from "firebase/auth";
import { auth } from "../config/firebase";
import mamuteLogo from "../assets/logo_mamute.png";

function traduzErroFirebase(code) {
  switch (code) {
    case "auth/invalid-email":
      return "E-mail inválido.";
    case "auth/user-not-found":
    case "auth/invalid-credential":
      return "Usuário ou senha incorretos.";
    case "auth/wrong-password":
      return "Senha incorreta.";
    case "auth/too-many-requests":
      return "Muitas tentativas. Aguarde um momento.";
    default:
      return "Erro ao entrar. Tente novamente.";
  }
}

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");
  const [carregando, setCarregando] = useState(false);
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [lembrar, setLembrar] = useState(false);
  const [animado, setAnimado] = useState(false);
  const [modoRecuperacao, setModoRecuperacao] = useState(false);

  useEffect(() => {
    setTimeout(() => setAnimado(true), 100);
    const emailSalvo = localStorage.getItem("mamute_email_lembrar");
    if (emailSalvo) {
      setEmail(emailSalvo);
      setLembrar(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");
    setCarregando(true);

    try {
      await login(email, senha);
      if (lembrar) {
        localStorage.setItem("mamute_email_lembrar", email);
      } else {
        localStorage.removeItem("mamute_email_lembrar");
      }
      navigate("/", { replace: true });
    } catch (error) {
      setErro(traduzErroFirebase(error.code));
    } finally {
      setCarregando(false);
    }
  };

  const handleRecuperarSenha = async (e) => {
    e.preventDefault();
    setErro("");
    setSucesso("");
    
    if (!email) {
      setErro("Digite seu e-mail para recuperar a senha.");
      return;
    }

    setCarregando(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setSucesso("E-mail de recuperação enviado! Verifique sua caixa de entrada.");
      setTimeout(() => setModoRecuperacao(false), 3000);
    } catch (error) {
      if (error.code === "auth/user-not-found") {
        setErro("E-mail não encontrado.");
      } else {
        setErro("Erro ao enviar e-mail. Tente novamente.");
      }
    } finally {
      setCarregando(false);
    }
  };

  // Fundo em SVG (tema manutenção): engrenagens + chave, bem sutil.
  const maintenanceBgSvg = encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="1200" height="800" viewBox="0 0 1200 800">
    <defs>
      <pattern id="p" width="200" height="200" patternUnits="userSpaceOnUse">
        <g fill="none" stroke="rgba(249,115,22,0.08)" stroke-width="1.5">
          <circle cx="100" cy="100" r="40"/>
          <circle cx="100" cy="100" r="25"/>
          <g transform="translate(100 100)">
            <line x1="0" y1="-40" x2="0" y2="-55"/>
            <line x1="0" y1="40" x2="0" y2="55"/>
            <line x1="-40" y1="0" x2="-55" y2="0"/>
            <line x1="40" y1="0" x2="55" y2="0"/>
            <g transform="rotate(45)">
              <line x1="0" y1="-40" x2="0" y2="-55"/>
              <line x1="0" y1="40" x2="0" y2="55"/>
              <line x1="-40" y1="0" x2="-55" y2="0"/>
              <line x1="40" y1="0" x2="55" y2="0"/>
            </g>
          </g>
        </g>
        <g fill="none" stroke="rgba(56,189,248,0.06)" stroke-width="1">
          <circle cx="30" cy="30" r="8"/>
          <circle cx="170" cy="170" r="12"/>
        </g>
      </pattern>
      <radialGradient id="glow" cx="30%" cy="30%" r="80%">
        <stop offset="0" stop-color="rgba(249,115,22,0.12)"/>
        <stop offset="0.4" stop-color="rgba(56,189,248,0.08)"/>
        <stop offset="1" stop-color="rgba(15,23,42,0)"/>
      </radialGradient>
    </defs>
    <rect width="1200" height="800" fill="url(#p)"/>
    <rect width="1200" height="800" fill="url(#glow)"/>
  </svg>
  `);

  const styles = {
    container: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, #0b1220 0%, #0f172a 45%, #0b1220 100%)",
      padding: "20px",
      position: "relative",
      overflow: "hidden",
    },
    bgEffect: {
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `
        radial-gradient(circle at 20% 80%, rgba(249, 115, 22, 0.1) 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, rgba(56, 189, 248, 0.08) 0%, transparent 50%)
      `,
      pointerEvents: "none",
    },
    bgSvg: {
      position: "absolute",
      inset: 0,
      backgroundImage: `url("data:image/svg+xml,${maintenanceBgSvg}")`,
      backgroundSize: "900px auto",
      backgroundRepeat: "repeat",
      opacity: 0.22,
      filter: "blur(0.2px)",
      pointerEvents: "none",
    },
    shell: {
      width: "100%",
      maxWidth: 980,
      display: "grid",
      gridTemplateColumns: "1.1fr 0.9fr",
      gap: 18,
      position: "relative",
      zIndex: 1,
    },
    brandPanel: {
      borderRadius: 22,
      padding: "34px 34px",
      background:
        "linear-gradient(135deg, rgba(2,6,23,0.72) 0%, rgba(15,23,42,0.62) 55%, rgba(2,6,23,0.72) 100%)",
      border: "1px solid rgba(255,255,255,0.10)",
      boxShadow: "0 28px 60px rgba(0,0,0,0.55)",
      backdropFilter: "blur(12px)",
      overflow: "hidden",
      position: "relative",
    },
    brandGlow: {
      position: "absolute",
      inset: -200,
      background:
        "radial-gradient(circle at 30% 30%, rgba(249,115,22,0.18), transparent 55%), radial-gradient(circle at 70% 70%, rgba(56,189,248,0.12), transparent 55%)",
      pointerEvents: "none",
    },
    brandTop: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      marginBottom: 22,
      position: "relative",
      zIndex: 1,
    },
    logo: {
      width: 58,
      height: 58,
      borderRadius: 16,
      padding: 10,
      background: "linear-gradient(135deg, rgba(249,115,22,0.15) 0%, rgba(234,88,12,0.08) 100%)",
      border: "1px solid rgba(249,115,22,0.35)",
      boxShadow: "0 12px 28px rgba(0,0,0,0.4), 0 0 20px rgba(249,115,22,0.15)",
      objectFit: "contain",
    },
    brandName: {
      display: "flex",
      flexDirection: "column",
      lineHeight: 1.1,
    },
    brandTitle: {
      margin: 0,
      color: "#fff",
      fontSize: 22,
      fontWeight: 800,
      letterSpacing: "-0.6px",
    },
    brandSub: {
      margin: 0,
      color: "rgba(226,232,240,0.78)",
      fontSize: 13,
    },
    heroTitle: {
      margin: 0,
      marginBottom: 14,
      color: "#fff",
      fontSize: 36,
      fontWeight: 800,
      letterSpacing: "-1px",
      lineHeight: 1.15,
      position: "relative",
      zIndex: 1,
      background: "linear-gradient(135deg, #ffffff 0%, #e2e8f0 50%, #f97316 100%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
    },
    heroDesc: {
      margin: 0,
      marginBottom: 26,
      color: "rgba(226,232,240,0.78)",
      fontSize: 15,
      lineHeight: 1.6,
      maxWidth: 420,
      position: "relative",
      zIndex: 1,
    },
    bullets: {
      display: "grid",
      gap: 12,
      position: "relative",
      zIndex: 1,
    },
    bullet: {
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
      padding: "14px 14px",
      borderRadius: 16,
      background: "rgba(2,6,23,0.40)",
      border: "1px solid rgba(255,255,255,0.08)",
    },
    bulletIcon: {
      width: 38,
      height: 38,
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(135deg, rgba(249,115,22,0.15) 0%, rgba(234,88,12,0.08) 100%)",
      border: "1px solid rgba(249,115,22,0.25)",
      fontSize: 18,
      flex: "0 0 auto",
    },
    bulletTitle: {
      margin: 0,
      color: "#fff",
      fontWeight: 700,
      fontSize: 14,
    },
    bulletText: {
      margin: 0,
      marginTop: 4,
      color: "rgba(226,232,240,0.72)",
      fontSize: 13,
      lineHeight: 1.5,
    },
    formCard: {
      width: "100%",
      background: "rgba(2, 6, 23, 0.72)",
      backdropFilter: "blur(12px)",
      padding: "32px 30px",
      borderRadius: 22,
      boxShadow: "0 28px 60px rgba(0,0,0,0.55)",
      border: "1px solid rgba(255,255,255,0.10)",
      transform: animado ? "translateY(0)" : "translateY(26px)",
      opacity: animado ? 1 : 0,
      transition: "all 0.6s cubic-bezier(0.16, 1, 0.3, 1)",
      position: "relative",
      zIndex: 1,
    },
    titulo: {
      margin: 0,
      marginBottom: 6,
      fontSize: 22,
      fontWeight: 800,
      color: "#fff",
      letterSpacing: "-0.6px",
    },
    subtitulo: {
      margin: 0,
      marginBottom: 22,
      fontSize: 13,
      color: "rgba(148,163,184,0.95)",
      lineHeight: 1.5,
    },
    inputGroup: {
      position: "relative",
      marginBottom: 18,
    },
    inputLabel: {
      display: "block",
      color: "#e2e8f0",
      fontSize: 13,
      fontWeight: 500,
      marginBottom: 6,
    },
    inputWrapper: {
      position: "relative",
      display: "flex",
      alignItems: "center",
    },
    inputIcon: {
      position: "absolute",
      left: 18,
      color: "#64748b",
      fontSize: 16,
      pointerEvents: "none",
      transition: "color 0.2s ease",
    },
    input: {
      width: "100%",
      paddingTop: 14,
      paddingBottom: 14,
      paddingLeft: 54,
      paddingRight: 16,
      borderRadius: 12,
      border: "1px solid rgba(148,163,184,0.35)",
      background: "linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.85) 100%)",
      color: "#f1f5f9",
      WebkitTextFillColor: "#f1f5f9",
      caretColor: "#f8fafc",
      fontSize: 15,
      outline: "none",
      transition: "all 0.25s ease",
      boxSizing: "border-box",
      boxShadow: "inset 0 2px 4px rgba(0,0,0,0.15)",
    },
    inputSenha: {
      paddingRight: 48,
    },
    btnMostrarSenha: {
      position: "absolute",
      right: 14,
      background: "none",
      border: "none",
      color: "#64748b",
      cursor: "pointer",
      padding: 4,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      transition: "color 0.2s ease",
    },
    checkboxContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 20,
      flexWrap: "wrap",
      gap: 10,
    },
    checkboxLabel: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      color: "#94a3b8",
      fontSize: 13,
      cursor: "pointer",
    },
    checkbox: {
      width: 18,
      height: 18,
      accentColor: "#f97316",
      cursor: "pointer",
    },
    linkEsqueci: {
      background: "none",
      border: "none",
      color: "#38bdf8",
      fontSize: 13,
      cursor: "pointer",
      textDecoration: "none",
      transition: "color 0.2s ease",
    },
    mensagem: {
      padding: "12px 14px",
      borderRadius: 10,
      marginBottom: 16,
      fontSize: 13,
      display: "flex",
      alignItems: "center",
      gap: 10,
    },
    erro: {
      background: "rgba(220, 38, 38, 0.15)",
      color: "#fca5a5",
      border: "1px solid rgba(220, 38, 38, 0.3)",
    },
    sucesso: {
      background: "rgba(34, 197, 94, 0.15)",
      color: "#86efac",
      border: "1px solid rgba(34, 197, 94, 0.3)",
    },
    btnPrimario: {
      width: "100%",
      padding: "14px 0",
      borderRadius: 12,
      border: "none",
      background: carregando 
        ? "#475569" 
        : "linear-gradient(135deg, #f97316 0%, #ea580c 100%)",
      color: "#fff",
      fontWeight: 600,
      fontSize: 16,
      cursor: carregando ? "not-allowed" : "pointer",
      transition: "all 0.3s ease",
      boxShadow: carregando ? "none" : "0 4px 15px rgba(249, 115, 22, 0.4)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
    },
    divider: {
      display: "flex",
      alignItems: "center",
      gap: 16,
      margin: "24px 0",
    },
    dividerLine: {
      flex: 1,
      height: 1,
      background: "rgba(255,255,255,0.1)",
    },
    dividerText: {
      color: "#64748b",
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: 1,
    },
    btnSecundario: {
      width: "100%",
      padding: "12px 0",
      borderRadius: 12,
      border: "1px solid rgba(148,163,184,0.25)",
      background: "rgba(2,6,23,0.35)",
      color: "#e2e8f0",
      fontWeight: 500,
      fontSize: 14,
      cursor: "pointer",
      transition: "all 0.2s ease",
    },
    spinner: {
      width: 20,
      height: 20,
      border: "2px solid rgba(255,255,255,0.3)",
      borderTopColor: "#fff",
      borderRadius: "50%",
      animation: "spin 0.8s linear infinite",
    },
  };

  const IconeEmail = () => (
    <svg style={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );

  const IconeCadeado = () => (
    <svg style={styles.inputIcon} width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  );

  const IconeOlho = ({ aberto }) => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {aberto ? (
        <>
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </>
      ) : (
        <>
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </>
      )}
    </svg>
  );

  const IconeErro = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="10"/>
      <line x1="15" y1="9" x2="9" y2="15"/>
      <line x1="9" y1="9" x2="15" y2="15"/>
    </svg>
  );

  const IconeSucesso = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
      <polyline points="22 4 12 14.01 9 11.01"/>
    </svg>
  );

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
          .login-input {
            color: #f1f5f9 !important;
            caret-color: #f8fafc !important;
            background: linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.85) 100%) !important;
            border-color: rgba(148,163,184,0.35) !important;
          }
          .login-input:-webkit-autofill,
          .login-input:-webkit-autofill:hover,
          .login-input:-webkit-autofill:focus {
            -webkit-text-fill-color: #f1f5f9 !important;
            caret-color: #f8fafc !important;
            box-shadow: 0 0 0px 1000px rgba(15,23,42,0.95) inset !important;
            border-color: rgba(148,163,184,0.35) !important;
          }
          .login-input::placeholder {
            color: #64748b;
            opacity: 1;
          }
          .login-input:focus {
            border-color: rgba(249, 115, 22, 0.85) !important;
            box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.18), inset 0 2px 4px rgba(0,0,0,0.1) !important;
            background: linear-gradient(135deg, rgba(15,23,42,1) 0%, rgba(30,41,59,0.95) 100%) !important;
          }
          .login-input:hover {
            border-color: rgba(148,163,184,0.55);
            background: linear-gradient(135deg, rgba(15,23,42,0.98) 0%, rgba(30,41,59,0.9) 100%);
          }
          .btn-primario:hover:not(:disabled) {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(249, 115, 22, 0.5) !important;
          }
          .btn-primario:active:not(:disabled) {
            transform: translateY(0);
          }
          .btn-secundario:hover {
            background: rgba(255,255,255,0.05) !important;
            border-color: #475569 !important;
          }
          .link-hover:hover {
            color: #7dd3fc !important;
            text-decoration: underline;
          }
          .btn-olho:hover {
            color: #f97316 !important;
          }
          .shadow-soft { box-shadow: 0 22px 60px rgba(0,0,0,0.55); }

          @keyframes float {
            0%, 100% { transform: translateY(0); }
            50% { transform: translateY(-8px); }
          }

          @keyframes pulse-glow {
            0%, 100% { box-shadow: 0 12px 28px rgba(0,0,0,0.4), 0 0 20px rgba(249,115,22,0.15); }
            50% { box-shadow: 0 12px 28px rgba(0,0,0,0.4), 0 0 35px rgba(249,115,22,0.25); }
          }

          .logo-animated {
            animation: pulse-glow 3s ease-in-out infinite;
          }

          .brand-panel-animated {
            animation: float 6s ease-in-out infinite;
          }

          @media (max-width: 900px) {
            .login-shell {
              grid-template-columns: 1fr !important;
            }
            .login-brand {
              display: none;
            }
          }
        `}
      </style>
      
      <div style={styles.bgEffect} />
      <div style={styles.bgSvg} />
      
      <div style={styles.shell} className="login-shell">
        {/* Painel de marca (desktop) */}
        <div style={styles.brandPanel} className="login-brand brand-panel-animated">
          <div style={styles.brandGlow} />
          <div style={styles.brandTop}>
            <img src={mamuteLogo} alt="Mamute" style={styles.logo} className="logo-animated" />
            <div style={styles.brandName}>
              <p style={styles.brandTitle}>Mamute</p>
              <p style={styles.brandSub}>Manutenção • OS • Preventivas • Indicadores</p>
            </div>
          </div>

          <h2 style={styles.heroTitle}>Manutenção com padrão profissional.</h2>
          <p style={styles.heroDesc}>
            Um painel feito para chão de fábrica: cadastros organizados, ordens de serviço, preventivas e histórico —
            tudo com consistência visual e foco em agilidade.
          </p>

          <div style={styles.bullets}>
            <div style={styles.bullet}>
              <div style={styles.bulletIcon}>🔧</div>
              <div>
                <p style={styles.bulletTitle}>Fluxo de OS na veia</p>
                <p style={styles.bulletText}>Abertas, finalizadas e execuções com visual limpo e ações rápidas.</p>
              </div>
            </div>
            <div style={styles.bullet}>
              <div style={styles.bulletIcon}>🧾</div>
              <div>
                <p style={styles.bulletTitle}>Preventivas e padrões</p>
                <p style={styles.bulletText}>Checklist consistente e fácil de evoluir página por página.</p>
              </div>
            </div>
            <div style={styles.bullet}>
              <div style={styles.bulletIcon}>📈</div>
              <div>
                <p style={styles.bulletTitle}>Visão e controle</p>
                <p style={styles.bulletText}>Base pronta para dashboards, indicadores e trilhas de melhoria.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Card de login */}
        <form
          onSubmit={modoRecuperacao ? handleRecuperarSenha : handleSubmit}
          style={styles.formCard}
          className="shadow-soft"
        >
          <h1 style={styles.titulo}>
            {modoRecuperacao ? "Recuperar acesso" : "Entrar"}
          </h1>
          <p style={styles.subtitulo}>
            {modoRecuperacao
              ? "Informe seu e-mail para receber o link de recuperação."
              : "Acesse o Mamute com sua conta."}
          </p>

          <div style={styles.inputGroup}>
            <label style={styles.inputLabel}>E-mail</label>
            <div style={styles.inputWrapper}>
              <IconeEmail />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="seu@email.com"
                autoComplete="email"
                className="login-input"
                style={styles.input}
              />
            </div>
          </div>

          {!modoRecuperacao && (
            <>
              <div style={styles.inputGroup}>
                <label style={styles.inputLabel}>Senha</label>
                <div style={styles.inputWrapper}>
                  <IconeCadeado />
                  <input
                    type={mostrarSenha ? "text" : "password"}
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    required
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="login-input"
                    style={{ ...styles.input, ...styles.inputSenha }}
                  />
                  <button
                    type="button"
                    onClick={() => setMostrarSenha(!mostrarSenha)}
                    style={styles.btnMostrarSenha}
                    className="btn-olho"
                    tabIndex={-1}
                    aria-label={mostrarSenha ? "Ocultar senha" : "Mostrar senha"}
                  >
                    <IconeOlho aberto={mostrarSenha} />
                  </button>
                </div>
              </div>

              <div style={styles.checkboxContainer}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={lembrar}
                    onChange={(e) => setLembrar(e.target.checked)}
                    style={styles.checkbox}
                  />
                  Lembrar e-mail
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setModoRecuperacao(true);
                    setErro("");
                    setSucesso("");
                  }}
                  style={styles.linkEsqueci}
                  className="link-hover"
                >
                  Esqueci minha senha
                </button>
              </div>
            </>
          )}

          {erro && (
            <div style={{ ...styles.mensagem, ...styles.erro }}>
              <IconeErro />
              {erro}
            </div>
          )}

          {sucesso && (
            <div style={{ ...styles.mensagem, ...styles.sucesso }}>
              <IconeSucesso />
              {sucesso}
            </div>
          )}

          <button
            type="submit"
            disabled={carregando}
            style={styles.btnPrimario}
            className="btn-primario"
          >
            {carregando ? (
              <>
                <div style={styles.spinner} />
                {modoRecuperacao ? "Enviando..." : "Entrando..."}
              </>
            ) : (
              modoRecuperacao ? "Enviar link" : "Entrar"
            )}
          </button>

          {modoRecuperacao ? (
            <button
              type="button"
              onClick={() => {
                setModoRecuperacao(false);
                setErro("");
                setSucesso("");
              }}
              style={{ ...styles.btnSecundario, marginTop: 14 }}
              className="btn-secundario"
            >
              Voltar
            </button>
          ) : (
            <>
              <div style={styles.divider}>
                <div style={styles.dividerLine} />
                <span style={styles.dividerText}>ou</span>
                <div style={styles.dividerLine} />
              </div>

              <button
                type="button"
                onClick={() => navigate("/cadastro-novo-usuario")}
                style={styles.btnSecundario}
                className="btn-secundario"
              >
                Criar nova conta
              </button>
            </>
          )}
        </form>
      </div>
    </div>
  );
}
